import { Inflate } from 'fflate';

const MiB = 1024 * 1024;

/** Product resource limits, applied before JSZip expands entries for CRC checking. */
const MAX_ARCHIVE_BYTES = 50 * MiB;
const MAX_EXPANDED_BYTES = 256 * MiB;
const MAX_ENTRIES = 10_000;
/**
 * Ceiling on the SUM of expanded XML parts (#1744). Measured 2026-09-24 on the
 * production image: a pathological 240 Mo XML part fit under the 256 Mo global
 * cap, was accepted as valid and cost 21 s and 916 Mo of heap to parse. A
 * 2 000-page text document holds only ~15 Mo of XML, so 64 Mo still admits
 * ~8 000 pages. It is a sum, not a per-part cap: `prepareDocx` parses every
 * XML part and keeps all trees alive together, so N parts of 63 Mo would cost
 * as much as one of N × 63 Mo. Media never count: they are not parsed.
 */
const MAX_EXPANDED_XML_BYTES = 64 * MiB;

/**
 * The parts `prepareDocx` decodes and parses as XML: `.xml` parts and `.rels`
 * relationship parts (both XML), case-insensitive as OPC part names are.
 * Shared so the ceiling counts exactly what the parser will read.
 */
export function isXmlPart(name: string): boolean {
  return /\.(xml|rels)$/i.test(name);
}

/** Bounded evidence of a `DOCX_XML_LIMIT` refusal: sizes and part names only, never content. */
export interface XmlLimitEvidence {
  readonly totalBytes: number;
  readonly largestPart: string;
  readonly largestBytes: number;
  readonly capBytes: number;
}

export class ArchiveFault extends Error {
  constructor(readonly code: string, readonly xmlLimit?: XmlLimitEvidence) { super(code); }
}

/**
 * Tracks expanded XML bytes and refuses past the ceiling, naming the largest
 * part: it is the one the lawyer (or support) must look at first.
 */
class XmlBudget {
  private total = 0;
  private readonly sizes = new Map<string, number>();

  add(name: string, bytes: number): void {
    if (!isXmlPart(name)) return;
    this.total += bytes;
    this.sizes.set(name, (this.sizes.get(name) ?? 0) + bytes);
    if (this.total <= MAX_EXPANDED_XML_BYTES) return;
    let [largestPart, largestBytes] = [name, 0];
    for (const [part, size] of this.sizes) if (size > largestBytes) [largestPart, largestBytes] = [part, size];
    throw new ArchiveFault('DOCX_XML_LIMIT', { totalBytes: this.total, largestPart, largestBytes, capBytes: MAX_EXPANDED_XML_BYTES });
  }
}

interface ZipEntry {
  readonly compressedOffset: number;
  readonly compressedSize: number;
  readonly crc32: number;
  readonly declaredSize: number;
  readonly method: number;
  readonly name: string;
}

export interface ZipDirectoryInspection {
  readonly entries: readonly ZipEntry[];
  readonly names: Set<string>;
}

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < table.length; index++) {
    let value = index;
    for (let bit = 0; bit < 8; bit++) value = value & 1 ? (value >>> 1) ^ 0xedb88320 : value >>> 1;
    table[index] = value >>> 0;
  }
  return table;
})();

function updateCrc32(crc: number, chunk: Uint8Array): number {
  let value = crc;
  for (const byte of chunk) value = crcTable[(value ^ byte) & 0xff]! ^ (value >>> 8);
  return value >>> 0;
}

/**
 * JSZip's name map silently replaces duplicate entries. Inspect the directory
 * first so duplicates, path traversal and oversized archives cannot become a
 * misleading successful validation. ZIP64/multi-volume packages are refused.
 */
export function inspectZipDirectory(bytes: Uint8Array): ZipDirectoryInspection {
  if (bytes.length > MAX_ARCHIVE_BYTES) throw new ArchiveFault('DOCX_ARCHIVE_LIMIT');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let offset = bytes.length - 22; offset >= Math.max(0, bytes.length - 65_557); offset--) {
    if (view.getUint32(offset, true) === 0x06054b50 && offset + 22 + view.getUint16(offset + 20, true) === bytes.length) {
      end = offset; break;
    }
  }
  if (end < 0 || view.getUint16(end + 4, true) || view.getUint16(end + 6, true)) {
    throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
  }
  const count = view.getUint16(end + 10, true);
  const directorySize = view.getUint32(end + 12, true);
  let cursor = view.getUint32(end + 16, true);
  const directoryEnd = cursor + directorySize;
  if (!count || count > MAX_ENTRIES || directoryEnd !== end || view.getUint16(end + 8, true) !== count) {
    throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
  }
  const names = new Set<string>();
  const folded = new Set<string>();
  const entries: ZipEntry[] = [];
  let expanded = 0;
  // Declared sizes: refuses an honest oversized package without inflating it.
  // verifyZipPayloads re-counts actual bytes against a forged directory.
  const xmlBudget = new XmlBudget();
  for (let index = 0; index < count; index++) {
    if (cursor + 46 > directoryEnd || view.getUint32(cursor, true) !== 0x02014b50) {
      throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
    }
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const crc32 = view.getUint32(cursor + 16, true);
    const compressed = view.getUint32(cursor + 20, true);
    const size = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    const next = cursor + 46 + nameLength + extraLength + commentLength;
    if (next > directoryEnd || flags & 1 || ![0, 8].includes(method) || size === 0xffffffff || compressed === 0xffffffff
      || view.getUint16(cursor + 34, true) !== 0 || localOffset + 30 > cursor) {
      throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
    }
    const rawName = bytes.subarray(cursor + 46, cursor + 46 + nameLength);
    const name = new TextDecoder('utf-8', { fatal: true }).decode(rawName);
    if (!name || name.startsWith('/') || name.includes('\\') || name.includes('\0') || name.split('/').some((part) => part === '..' || part === '.')
      || folded.has(name.toLowerCase())) throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
    if (view.getUint32(localOffset, true) !== 0x04034b50) throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLength + localExtraLength;
    if (dataStart + compressed > view.getUint32(end + 16, true)
      || new TextDecoder('utf-8', { fatal: true }).decode(bytes.subarray(localOffset + 30, localOffset + 30 + localNameLength)) !== name) {
      throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
    }
    expanded += size;
    if (expanded > MAX_EXPANDED_BYTES) throw new ArchiveFault('DOCX_ARCHIVE_LIMIT');
    xmlBudget.add(name, size);
    entries.push({ compressedOffset: dataStart, compressedSize: compressed, crc32, declaredSize: size, method, name });
    names.add(name); folded.add(name.toLowerCase()); cursor = next;
  }
  if (cursor !== directoryEnd) throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
  return { names, entries };
}

/**
 * Compressed bytes handed to fflate per step. fflate's `Inflate.push` inflates
 * everything it is given before calling back (fflate 0.8.3 `Inflate.c`), and
 * DEFLATE expands at most ~1 032×: pushing a whole entry at once would
 * materialise it entirely before any count (#1744 review: 160 Mo in one
 * callback). 16 Kio caps one step at ~16,5 Mo, below the 64 Mo XML budget;
 * 64 Kio could already yield 66 Mo in a single step.
 */
const INFLATE_INPUT_SLICE = 16 * 1024;

/**
 * The central-directory size is attacker-controlled. Inflate every member in
 * bounded steps before JSZip gets the archive: compressed input is pushed in
 * `INFLATE_INPUT_SLICE` slices and each step's output is counted against the
 * XML budget and the global cap, so exceeding either aborts mid-entry, before
 * the full payload is ever produced. We also prove its actual CRC and length;
 * JSZip's later CRC pass then cannot be the first unbounded decompression.
 */
export function verifyZipPayloads(bytes: Uint8Array, inspection: ZipDirectoryInspection): void {
  let expanded = 0;
  const xmlBudget = new XmlBudget();
  for (const entry of inspection.entries) {
    let actualSize = 0;
    let crc = 0xffffffff;
    const consume = (chunk: Uint8Array): void => {
      actualSize += chunk.byteLength;
      expanded += chunk.byteLength;
      if (actualSize > MAX_EXPANDED_BYTES || expanded > MAX_EXPANDED_BYTES) {
        throw new ArchiveFault('DOCX_ARCHIVE_LIMIT');
      }
      xmlBudget.add(entry.name, chunk.byteLength);
      crc = updateCrc32(crc, chunk);
    };
    const compressed = bytes.subarray(entry.compressedOffset, entry.compressedOffset + entry.compressedSize);
    try {
      if (entry.method === 0) {
        consume(compressed);
      } else {
        let completed = false;
        const inflate = new Inflate((chunk, final) => {
          consume(chunk);
          completed = final;
        });
        // Always at least one push, so an empty deflate stream still reaches `final`.
        for (let offset = 0; offset === 0 || offset < compressed.length; offset += INFLATE_INPUT_SLICE) {
          const next = offset + INFLATE_INPUT_SLICE;
          inflate.push(compressed.subarray(offset, next), next >= compressed.length);
        }
        if (!completed) throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
      }
    } catch (error) {
      if (error instanceof ArchiveFault) throw error;
      throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
    }
    if (actualSize !== entry.declaredSize || ((crc ^ 0xffffffff) >>> 0) !== entry.crc32) {
      throw new ArchiveFault('DOCX_ARCHIVE_INVALID');
    }
  }
}
